import clsx from 'clsx';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { Box, Paragraph, Text } from '@customerio/pluma-components/react';
import { ShimmerText } from '@/components/feedback/shimmer-text';
import { useCyclingLabel } from './use-cycling-label';
import * as styles from './index.css';

const WORD_SWAP_TRANSITION = { duration: 0.25 } as const;

type BrandLoaderProps = {
  label?: string;
  labels?: readonly string[];
  /**
   * Render inside a host that already owns viewport height and background
   * (the signup shell). Standalone is the default: the loader is the page.
   */
  isEmbedded?: boolean;
  isImmediate?: boolean;
};

export function BrandLoader({ label, labels, isEmbedded = false, isImmediate = false }: BrandLoaderProps) {
  const shouldReduceMotion = useReducedMotion();
  const cycled = useCyclingLabel(labels ?? [], shouldReduceMotion === true);
  const shownLabel = cycled ?? label;
  const isCycling = (labels?.length ?? 0) > 1;
  const screenClassName = clsx(
    isEmbedded ? styles.screenEmbedded : styles.screenStandalone,
    !isImmediate && styles.screenDelayedAppear,
  );
  return (
    <Box className={screenClassName} role="status" aria-live="polite">
      <Box className={styles.stage}>
        <Box className={styles.bloomPrimary} aria-hidden="true" />
        <Box className={styles.bloomSecondary} aria-hidden="true" />
        <svg
          className={styles.mark}
          width="96"
          height="96"
          viewBox="0 0 27 27"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            className={styles.markSegment}
            d="M26.7922 6.81435L20.0935 0.115646H13.3948V6.81435L20.0935 13.5105L13.3948 20.2092V26.9079H20.0935L26.7922 20.2092V6.81435Z"
          />
          <path
            className={`${styles.markSegment} ${styles.markSegmentDelay1}`}
            d="M6.6987 13.5105L13.3948 6.81437L10.0468 3.46373L6.6987 0.115669H0V6.81437L3.34806 10.1624L6.6987 13.5105Z"
          />
          <path
            className={`${styles.markSegment} ${styles.markSegmentDelay2}`}
            d="M3.34806 16.8611L0 20.2092V26.9079H6.6987L10.0468 23.5573L13.3948 20.2092L6.6987 13.5105L3.34806 16.8611Z"
          />
        </svg>
      </Box>
      {/* A cycling label loops for as long as the loader is mounted, and this
          is a polite live region, so announcing the visible text would repeat
          every tick. Announce once and leave the loop visual-only. A single
          label never changes, so it stays the announcement. */}
      {isCycling ? <Box visuallyHidden={true}>{labels?.[0]}</Box> : null}
      {shownLabel == null ? (
        // Everything else here is aria-hidden, so without this the live region
        // announces nothing and the loading state is silent.
        <Box visuallyHidden={true}>Loading…</Box>
      ) : (
        <Box position="relative" aria-hidden={isCycling ? true : undefined}>
          <AnimatePresence mode="wait">
            <m.div
              key={shownLabel}
              initial={shouldReduceMotion === true ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={WORD_SWAP_TRANSITION}
            >
              {shouldReduceMotion === true ? (
                <Paragraph variant="secondary" color="base">
                  {shownLabel}
                </Paragraph>
              ) : (
                <Text as={ShimmerText} text="product-p">
                  {shownLabel}
                </Text>
              )}
            </m.div>
          </AnimatePresence>
        </Box>
      )}
    </Box>
  );
}
